/**
 * Scans the current DOM for invalid Angular Material form fields
 * and returns a list of their labels. Call this AFTER raising
 * form validations (so `ng-invalid` classes are applied).
 */
export function collectInvalidFieldLabels(): string[] {
  const labels = new Set<string>();

  // Angular Material marks invalid fields with the `mat-form-field-invalid` class
  const invalidFields = document.querySelectorAll(
    '.mat-form-field-invalid, .mat-mdc-form-field.ng-invalid, .mdc-text-field--invalid'
  );

  invalidFields.forEach((field) => {
    // Try to read the mat-label text
    const labelEl = field.querySelector('mat-label, label');
    const label = labelEl?.textContent?.trim();
    if (label) {
      labels.add(label);
      return;
    }

    // Fallback: aria-label on the input
    const input = field.querySelector('input, textarea, select, mat-select');
    const aria = input?.getAttribute('aria-label')?.trim();
    if (aria) {
      labels.add(aria);
      return;
    }

    // Fallback: placeholder
    const placeholder = (input as HTMLInputElement | null)?.placeholder?.trim();
    if (placeholder) {
      labels.add(placeholder);
    }
  });

  // Native invalid form controls (e.g. plain <input required> without mat-form-field)
  document
    .querySelectorAll('input.ng-invalid, textarea.ng-invalid, select.ng-invalid')
    .forEach((el) => {
      const name = el.getAttribute('aria-label') || el.getAttribute('placeholder') || el.getAttribute('formcontrolname');
      if (name && name.trim()) labels.add(name.trim());
    });

  return Array.from(labels);
}

/**
 * Builds a user-friendly validation error message listing the invalid fields.
 */
export function buildValidationErrorMessage(fallback = 'Some required details are missing in the form'): string {
  const fields = collectInvalidFieldLabels();
  if (fields.length === 0) {
    return fallback;
  }
  if (fields.length === 1) {
    return `The following field is required or invalid: ${fields[0]}`;
  }
  if (fields.length <= 6) {
    return `The following fields are required or invalid:\n\n• ${fields.join('\n• ')}`;
  }
  // Too many — show first 6 and a count
  return `The following fields are required or invalid:\n\n• ${fields.slice(0, 6).join('\n• ')}\n\n…and ${fields.length - 6} more.`;
}
